import { Op } from "./ws-envelope";

/**
 * Minimal snapshot of the locally-stored row an incoming remote op targets.
 * `undefined` means there is no local row for that rowId yet.
 */
export interface StoredRowSnapshot {
  updatedAt: number;
  deleted?: 0 | 1;
  obsolete?: 0 | 1;
}

/** The applying device's own identity, used for the tenant guard. */
export interface ApplyCtx {
  myRegistrationId: string;
}

export type ApplyDecision = "apply" | "skip-stale" | "skip-cross-tenant" | "skip-missing-target";

/**
 * Decide whether a remote op (REMOTE / BACKFILL) should be applied into the
 * local SQLite store. PURE — no I/O. Mirrors the server `applyOps` guards on
 * the client side: tenant isolation, last-write-wins on `updatedAt`, and
 * missing-target for obsolete/delete tombstones.
 *
 * No rule reads `originDeviceId`. The server stores the CREATOR of a row there
 * and a later write does not change it, so a catch-up row that a peer deleted
 * or changed still names the device that created it. A rule that skipped such
 * a row made the creator keep its old copy for good (#963). The device's own
 * write needs no rule: its stamp equals the stored one, so rule 3 skips it.
 *
 * Do not skip "an upsert of my own row that I no longer hold" either. A missing
 * row is no proof that this device removed it: a peer can have written the row
 * again (duty 0 is one row for an aircraft), and a device whose database was
 * wiped must get its rows back.
 *
 * Rule order:
 *  1. Cross-tenant: non-empty registrationId !== myRegistrationId → skip-cross-tenant
 *  2. Missing-target: non-upsert op with no stored row → skip-missing-target
 *  3. Stale (LWW): stored exists AND opUpdatedAt <= stored.updatedAt → skip-stale
 *  4. Otherwise → apply
 */
export function classifyRemoteOp(op: Op, stored: StoredRowSnapshot | undefined, ctx: ApplyCtx): ApplyDecision {
  // Rule 1: cross-tenant (only when registrationId is a non-empty string)
  const regId = op.row["registrationId"];
  if (typeof regId === "string" && regId !== "" && regId !== ctx.myRegistrationId) {
    return "skip-cross-tenant";
  }

  // Rule 2: missing-target (non-upsert with no stored row)
  if (op.type !== "upsert" && stored === undefined) {
    return "skip-missing-target";
  }

  // Rule 3: stale LWW (only when stored exists)
  if (stored !== undefined) {
    const opUpdatedAt = Number(op.row["updatedAt"]);
    if (!(opUpdatedAt > stored.updatedAt)) {
      return "skip-stale";
    }
  }

  // Rule 4: apply
  return "apply";
}
